/*
This class is accessible as window.tipLink
*/
import { VersionedTransaction } from "@solana/web3.js";
import { PublicKey } from "@solana/web3.js";
import { Transaction } from "@solana/web3.js";
import { documentReady, htmlToElement } from "./embedUtils.js";
import { getCloseButtonUrl, getTipLinkUrl } from "./utils.js";
import { CallbackType, WindowCommunicator } from "./window.js";
import { EventEmitter, WalletSendTransactionError, WalletSignTransactionError, isVersionedTransaction, } from "@solana/wallet-adapter-base";
import { SolanaSignAndSendTransaction, SolanaSignTransaction, SolanaSignMessage, SolanaSignIn, } from "@solana/wallet-standard-features";
import { createSignInMessage, } from "@solana/wallet-standard-util";
import { v4 as uuid } from "uuid";
import { NPM_VERSION } from "./index.js";
import { SOLANA_MAINNET_CHAIN } from "@solana/wallet-standard-chains";
import { ReadonlyWalletAccount } from "@wallet-standard/wallet";
import { showDialog } from "./dialog.js";
export const iframePath = "embedded_wallet";
export var PopupType;
(function (PopupType) {
    PopupType[PopupType["MESSAGE"] = 0] = "MESSAGE";
    PopupType[PopupType["TRANSACTION"] = 1] = "TRANSACTION";
    PopupType[PopupType["LOGIN"] = 2] = "LOGIN";
    PopupType[PopupType["NOT_ALLOWLISTED"] = 3] = "NOT_ALLOWLISTED";
})(PopupType || (PopupType = {}));
export class TipLinkEmbed extends EventEmitter {
    constructor(title, buildEnv, clientId, forceIframe, dAppSessionId, isDisallowed) {
        super();
        this.extendSession = () => {
            if (!this.tipLinkIframe?.contentWindow) {
                return;
            }
            this.windowCommunicator.singlePostToWindow(this.tipLinkIframe.contentWindow, {
                message: "extend_session",
            });
        };
        this.popupTypeMessage = (popupType) => {
            const baseUrl = getTipLinkUrl(this.buildEnv);
            switch (popupType) {
                case PopupType.MESSAGE:
                    return {
                        title: "Click below to view message:",
                        description: "",
                        popupImgUrl: new URL("adapter-message.svg", baseUrl).toString(),
                        buttonText: "View Message",
                    };
                case PopupType.TRANSACTION:
                    return {
                        title: "View Transaction",
                        description: "Click below to view and confirm your transaction:",
                        popupImgUrl: new URL("adapter-wallet.svg", baseUrl).toString(),
                        buttonText: "View Transaction",
                    };
                case PopupType.LOGIN:
                    return {
                        title: "Login",
                        description: "Click below to login:",
                        popupImgUrl: new URL("adapter-wallet.svg", baseUrl).toString(),
                        buttonText: "Login",
                    };
                case PopupType.NOT_ALLOWLISTED:
                    return {
                        title: "Application not allowlisted",
                        description: "Your application has not been allowlisted for the TipLink Wallet Adapter. Please reach out to contact@tiplink.io for support",
                    };
            }
        };
        this.showTipLinkAutoconnectToast = () => {
            this._showSuccessToast("Your TipLink wallet is connected!");
        };
        this.postToIframe = (data) => {
            if (this.tipLinkIframe?.contentWindow) {
                this.windowCommunicator.singlePostToWindow(this.tipLinkIframe.contentWindow, {
                    ...data,
                    title: this.title,
                });
            }
        };
        this.showIframe = () => {
            if (this.tipLinkIframe) {
                this.tipLinkIframe.style.display = "block";
            }
        };
        this.title = title;
        this.tipLinkAlertContainer = undefined;
        this.tipLinkToastContainer = undefined;
        const prevPublicKey = localStorage.getItem("tipLink_pk_connected");
        this.publicKeyString = prevPublicKey || undefined;
        this.buildEnv = buildEnv;
        this.clientId = clientId;
        this.windowCommunicator = new WindowCommunicator(this.buildEnv, (onClick, onClose, popupType) => {
            this._createPopupBlockAlert(onClick, onClose, popupType);
        });
        const cssLink = new URL("/css/widget.css", getTipLinkUrl(this.buildEnv));
        this.styleLink = htmlToElement(`<link href="${cssLink.toString()}" rel="stylesheet" type="text/css">`);
        this.forceIframe = forceIframe;
        this.isDisallowed = isDisallowed;
        this.dAppSessionId = dAppSessionId;
        this.tipLinkSessionId = uuid();
    }
    get publicKey() {
        if (this.publicKeyString) {
            return new PublicKey(this.publicKeyString);
        }
        return null;
    }
    get isLoggedIn() {
        return !!this.publicKeyString;
    }
    hideIframe() {
        if (this.tipLinkIframe?.style?.display) {
            this.tipLinkIframe.style.display = "none";
        }
    }
    getGreenCheckmarkLogoUrl() {
        return new URL("adapter-green-checkmark.svg", getTipLinkUrl(this.buildEnv)).toString();
    }
    getTipLinkLogoUrl() {
        const logoUrl = new URL("adapter-tiplink-logo.svg", getTipLinkUrl(this.buildEnv));
        return logoUrl.toString();
    }
    getGoogleLogoUrl() {
        const logoUrl = new URL("adapter-google-logo.svg", getTipLinkUrl(this.buildEnv));
        return logoUrl.toString();
    }
    getBackgroundImageUrl() {
        const logoUrl = new URL("adapter-modal-background.png", getTipLinkUrl(this.buildEnv));
        return logoUrl.toString();
    }
    fadeIn(element) {
        element.classList.remove("fade-out");
        element.classList.add("fade-in");
        element.style.display = "flex";
    }
    fadeOut(element, onFinished) {
        element.classList.remove("fade-in");
        element.classList.add("fade-out");
        const listener = (event) => {
            if (event.animationName === "fadeOut") {
                element.style.display = "none"; // Set display to none after fade-out
                element.removeEventListener("animationend", listener);
                onFinished();
            }
        };
        element.addEventListener("animationend", listener);
    }
    _showSuccessToast(text) {
        const innerContainer = htmlToElement(`<div id='tipLinkSuccessToast__inner_container'>
        <img id="tipLinkSuccessCheckmark__logo" src="${this.getGreenCheckmarkLogoUrl()}" />
        <p>${text}</p>
      </div>`);
        const removeToast = (element) => {
            if (element) {
                element.remove();
                if (this.tipLinkToastContainer &&
                    this.tipLinkToastContainer.children.length === 0) {
                    this.tipLinkToastContainer.style.display = "none";
                }
            }
        };
        const attachOnLoad = () => {
            if (this.tipLinkToastContainer) {
                innerContainer.addEventListener("click", () => {
                    removeToast(innerContainer);
                });
                this.tipLinkToastContainer.appendChild(innerContainer);
            }
        };
        attachOnLoad();
        if (this.tipLinkToastContainer) {
            this.tipLinkToastContainer.style.display = "block";
            this.fadeIn(innerContainer);
            setTimeout(() => {
                if (innerContainer) {
                    this.fadeOut(innerContainer, () => {
                        removeToast(innerContainer);
                    });
                }
            }, 10000);
        }
    }
    async _createPopupBlockAlert(onClick, onClose, popupType) {
        const modal = htmlToElement("<div id='tipLinkAlertModal'></div>");
        const innerContainer = htmlToElement("<div id='tipLinkAlert__inner_container'></div>");
        const overlay = htmlToElement("<div id='tipLinkAlert__modal_overlay'></div>");
        const tipLinkAlert = htmlToElement('<div id="tipLinkAlert" class="tipLink-alert--v2"></div>');
        const { description, title, popupImgUrl, buttonText } = this.popupTypeMessage(popupType);
        const tipLinkMessageAndButtonContainer = htmlToElement('<div id="tipLinkAlert__message_btn_container">' +
            `<div id="tipLinkAlert__message_container">` +
            `${popupImgUrl
                ? `<div id="tipLinkLogo_container"><img id="tipLinkAlert_logo" src="${popupImgUrl}"></img></div>`
                : ""}` +
            `<h1 id="tipLinkAlert__title">${title}</h1>` +
            `${description ? `<p id="tipLinkAlert__desc">${description}</p>` : ""}` +
            "</div>" +
            "</div>");
        const successAlert = htmlToElement(`<div><button id="tipLinkAlert__btn">${buttonText}</button></div>`);
        const btnContainer = htmlToElement('<div id="tipLinkAlert__btn-container"></div>');
        const closeButtonIconUrl = getCloseButtonUrl(this.buildEnv);
        const closeButton = htmlToElement(`<div id="tipLinkAlert__close-btn"><img id="tipLinkAlert__close-btn-image" src="${closeButtonIconUrl}"/></div>`);
        if (buttonText) {
            btnContainer.appendChild(successAlert);
            tipLinkMessageAndButtonContainer.appendChild(btnContainer);
        }
        tipLinkAlert.appendChild(tipLinkMessageAndButtonContainer);
        innerContainer.appendChild(tipLinkAlert);
        innerContainer.appendChild(closeButton);
        modal.appendChild(innerContainer);
        modal.appendChild(overlay);
        const removePopupBlockedAlert = () => {
            modal.remove();
            if (this.tipLinkAlertContainer &&
                this.tipLinkAlertContainer.children.length === 0) {
                this.tipLinkAlertContainer.style.display = "none";
            }
        };
        const bindOnLoad = () => {
            btnContainer.addEventListener("click", () => {
                onClick();
                removePopupBlockedAlert();
            });
        };
        const bindCloseButton = () => {
            closeButton.addEventListener("click", () => {
                removePopupBlockedAlert();
                onClose();
            });
            overlay.addEventListener("mousedown", () => {
                removePopupBlockedAlert();
                onClose();
            });
        };
        const attachOnLoad = () => {
            if (this.tipLinkAlertContainer) {
                this.tipLinkAlertContainer.appendChild(modal);
            }
        };
        attachOnLoad();
        bindCloseButton();
        if (buttonText) {
            bindOnLoad();
        }
        if (this.tipLinkAlertContainer) {
            this.tipLinkAlertContainer.style.display = "block";
        }
    }
    getQueryParams() {
        const params = {};
        const queryString = window.location.search.slice(1);
        queryString.split("&").forEach((pair) => {
            const [key, value] = pair.split("=");
            params[decodeURIComponent(key)] = decodeURIComponent(value);
        });
        return params;
    }
    async init({ directConnect, autoConnect: autoConnect, forceClickToContinue: forceClickToContinue, showErrorMessage, siwsInput, theme, }) {
        if (this.isDisallowed()) {
            this.notifyDisallowed();
            return Promise.reject("Application not allowlisted");
        }
        const queryParams = this.getQueryParams();
        const promptTipLinkAutoConnectFromRedirect = !!queryParams.promptTipLinkAutoConnect;
        const tipLinkAutoConnect = !!queryParams.tipLinkAutoConnect;
        if (promptTipLinkAutoConnectFromRedirect || tipLinkAutoConnect) {
            directConnect = false;
            autoConnect = true;
        }
        let windowParams = undefined;
        const isThemed = theme !== "system";
        if (directConnect && !promptTipLinkAutoConnectFromRedirect) {
            windowParams = this.windowCommunicator.openPopup(`/embedded_adapter_login?ref=${window.location.origin}${isThemed ? `&theme=${theme}` : ""}`);
            if (!windowParams.popup || windowParams.popup.closed) {
                directConnect = false;
                forceClickToContinue = true;
            }
        }
        const siwsInputPromise = typeof siwsInput === "function"
            ? siwsInput()
            : siwsInput
                ? Promise.resolve(siwsInput)
                : undefined;
        const tipLinkUrl = getTipLinkUrl(this.buildEnv);
        const tipLinkIframeUrl = new URL(iframePath, tipLinkUrl);
        tipLinkIframeUrl.searchParams.append("c", this.clientId);
        tipLinkIframeUrl.searchParams.append("ref", encodeURI(window.location.origin));
        tipLinkIframeUrl.searchParams.append("v", NPM_VERSION);
        if (autoConnect) {
            tipLinkIframeUrl.searchParams.append("autoConnect", "true");
        }
        if (tipLinkAutoConnect) {
            tipLinkIframeUrl.searchParams.append("tipLinkAutoConnect", "true");
        }
        if (theme && isThemed) {
            tipLinkIframeUrl.searchParams.append("theme", theme);
        }
        this.tipLinkIframe = htmlToElement(`<iframe
        id="tipLinkIframe"
        class="tipLinkIframe"
        allowtransparency="true"
        src="${tipLinkIframeUrl.href}"
        style="display: none; position: fixed; top: 0; right: 0; width: 100%; background-color: transparent;
        height: 100%; border: none; border-radius: 0; z-index: 2147483647; color-scheme: light; pointer-events: auto;"
      ></iframe>`);
        const cssLink = new URL("/css/widget.css", getTipLinkUrl(this.buildEnv));
        this.styleLink = htmlToElement(`<link href="${cssLink.toString()}" rel="stylesheet" type="text/css">`);
        this.tipLinkAlertContainer = htmlToElement(`<div id="tipLinkAlertContainer" style="display:none; z-index: 2147483647"></div>`);
        this.tipLinkToastContainer = htmlToElement(`<div id="tipLinkToastContainer" style="display:none; z-index: 2147483647"></div>`);
        let fnsAtEnd = [];
        let checkPopupClosed = undefined;
        let checkUrlForPausedExecution = undefined;
        let iframeNotLoading = undefined;
        let doCheckUrlForPausedExecution = false;
        fnsAtEnd.push(() => {
            clearInterval(checkPopupClosed);
            clearInterval(checkUrlForPausedExecution);
            clearInterval(iframeNotLoading);
        });
        const handleSetup = async () => {
            return new Promise((resolve, reject) => {
                // console.log("actually RUNNING handle setup");
                if (directConnect) {
                    checkUrlForPausedExecution = setInterval(() => {
                        if (!doCheckUrlForPausedExecution) {
                            return;
                        }
                        try {
                            const url = this.tipLinkIframe?.contentWindow?.document.URL;
                            if (url === "about:blank") {
                                console.error("iframe is not loading");
                                // it is possible in mobile safari that the iframe in the background tab doesn't
                                // finish loading before the new window is opened. The window will then auto-close
                                // due to a timeout, and we'll show the login page instead.
                                clearInterval(checkUrlForPausedExecution);
                                iframeNotLoading = setInterval(() => {
                                    windowParams?.popup.postMessage({ type: "iframe_not_loading" }, getTipLinkUrl(this.buildEnv));
                                }, 1000);
                            }
                        }
                        catch (error) {
                            // best effort, no need to handle
                        }
                    }, 300);
                    checkPopupClosed = setInterval(() => {
                        if (!windowParams?.popup || windowParams?.popup.closed) {
                            clearInterval(checkPopupClosed);
                            try {
                                const url = this.tipLinkIframe?.contentWindow?.document?.URL;
                                if (url === "about:blank") {
                                    fnsAtEnd.forEach((fn) => fn());
                                    fnsAtEnd = [];
                                    this.clearElements();
                                    this.init({
                                        directConnect: false,
                                        autoConnect: true,
                                        forceClickToContinue: true,
                                        showErrorMessage: true,
                                        theme,
                                        siwsInput,
                                    })
                                        .then((result) => {
                                        resolve(result);
                                    })
                                        .catch((error) => {
                                        reject(error);
                                    });
                                    return;
                                }
                            }
                            catch {
                                // best effort, no need to handle
                            }
                            if (this.tipLinkIframe?.contentWindow) {
                                this.windowCommunicator.singlePostToWindow(this.tipLinkIframe.contentWindow, {
                                    type: "click_to_continue",
                                    title: this.title,
                                });
                                this.showIframe();
                            }
                        }
                    }, 300);
                }
                try {
                    if (this.tipLinkIframe === undefined) {
                        throw Error("tipLinkIframe is undefined");
                    }
                    if (this.tipLinkAlertContainer === undefined) {
                        throw Error("tipLinkAlertContainer is undefined");
                    }
                    if (this.tipLinkToastContainer === undefined) {
                        throw Error("tipLinkToastContainer is undefined");
                    }
                    if (this.styleLink === undefined) {
                        throw Error("tipLinkStyles is undefined");
                    }
                    // const start = Date.now();
                    // console.log("appending child to iframe at:", start);
                    window.document.head.appendChild(this.styleLink);
                    window.document.body.appendChild(this.tipLinkIframe);
                    window.document.body.appendChild(this.tipLinkAlertContainer);
                    window.document.body.appendChild(this.tipLinkToastContainer);
                    if (this.isDisallowed()) {
                        this.notifyDisallowed();
                        windowParams?.popup?.close();
                        reject("Application not allowlisted");
                        return;
                    }
                    doCheckUrlForPausedExecution = true;
                    let requestAnimationFrameTimeout = undefined;
                    const callback = (timestamp) => {
                        // console.log("timestamp", timestamp);
                        if (timestamp) {
                            clearTimeout(requestAnimationFrameTimeout);
                            doCheckUrlForPausedExecution = false;
                            setTimeout(() => {
                                // console.log(
                                //   "started nested request animation frame",
                                //   Date.now()
                                // );
                                // It is possible that the iframe loads a bit at first,
                                // so we set doCheckUrlForPausedExecution to false. However,
                                // we try requesting animation frame again shortly after
                                // and if times out, we set doCheckUrlForPausedExecution to true
                                // so that in the `checkUrlForPausedExecution` interval above it
                                // will indeed check. Note that we're not overly worried about
                                // this flag changing back to true unncessarily because we have the
                                // iframe url check against about:blank in the `checkUrlForPausedExecution`
                                // interval above
                                requestAnimationFrameTimeout = setTimeout(() => {
                                    // console.log("Hit timeout at", Date.now());
                                    doCheckUrlForPausedExecution = true;
                                }, 500);
                                window.requestAnimationFrame(callback);
                            }, 300);
                        }
                    };
                    window.requestAnimationFrame(callback);
                    this.tipLinkIframe.addEventListener("load", async () => {
                        if (!this.tipLinkIframe) {
                            throw Error("tipLinkIframe is undefined");
                        }
                        if (this.tipLinkIframe.contentWindow === null) {
                            throw Error("tipLinkIframe.contentWindow is null");
                        }
                        this.showIframe();
                        let postReadyInterval = undefined;
                        let windowPost = undefined;
                        if (windowParams?.popup && !windowParams.popup.closed) {
                            const { post, close } = 
                            // TODO: this abstraction kind of sucks, this should not require an await
                            await this.windowCommunicator.setupHandshakeWithWindowParams(windowParams, {
                                window_ack: {
                                    type: CallbackType.DEFAULT,
                                    cb: async (data) => {
                                        // console.log("received window ack", data);
                                        clearInterval(postReadyInterval);
                                    },
                                },
                                done: {
                                    type: CallbackType.DEFAULT,
                                    cb: async (data) => {
                                        // console.log("!!received done", data);
                                        this.showIframe();
                                        close();
                                    },
                                },
                            }, ["done"]);
                            // console.log("got window post function!!");
                            windowPost = post;
                        }
                        // console.log("!!! setting up handhsake with iframe!!");
                        const { close: closeIframeChannel } = this.windowCommunicator.setupHandshakeWithIframe(this.tipLinkIframe, {
                            ready: {
                                type: CallbackType.DEFAULT,
                                cb: async (data) => {
                                    // console.log('received "ready" message from iframe', data);
                                    if (this.tipLinkIframe?.contentWindow) {
                                        this.windowCommunicator.singlePostToWindow(this.tipLinkIframe.contentWindow, {
                                            type: "ack",
                                            title: this.title,
                                            dAppSessionId: this.dAppSessionId,
                                            tipLinkSessionId: this.tipLinkSessionId,
                                        });
                                    }
                                    // TODO: use better flag to send to window post
                                    // console.log("sending ready messages to window!");
                                    windowPost?.({
                                        type: "ready",
                                        dAppSessionId: this.dAppSessionId,
                                        tipLinkSessionId: this.tipLinkSessionId,
                                    });
                                    if (windowPost) {
                                        postReadyInterval = setInterval(() => {
                                            windowPost?.({
                                                type: "ready",
                                                dAppSessionId: this.dAppSessionId,
                                                tipLinkSessionId: this.tipLinkSessionId,
                                            });
                                        }, 200);
                                    }
                                    if ((directConnect &&
                                        (!windowParams?.popup ||
                                            windowParams.popup.closed)) ||
                                        forceClickToContinue) {
                                        clearInterval(checkPopupClosed);
                                        if (this.tipLinkIframe?.contentWindow) {
                                            this.windowCommunicator.singlePostToWindow(this.tipLinkIframe.contentWindow, {
                                                type: "click_to_continue",
                                                showErrorMessage,
                                                title: this.title,
                                            });
                                        }
                                        this.showIframe();
                                    }
                                    else if (!directConnect &&
                                        !promptTipLinkAutoConnectFromRedirect) {
                                        clearInterval(checkPopupClosed);
                                        // console.log("TRYING TO SINGLE POST!");
                                        if (this.tipLinkIframe?.contentWindow) {
                                            this.windowCommunicator.singlePostToWindow(this.tipLinkIframe.contentWindow, {
                                                type: "embedded_login",
                                            });
                                        }
                                        // console.log(
                                        //   "displaying iframe for embedded_login after",
                                        //   Date.now() - start
                                        // );
                                        this.showIframe();
                                    }
                                    else if (promptTipLinkAutoConnectFromRedirect) {
                                        clearInterval(checkPopupClosed);
                                        if (this.tipLinkIframe?.contentWindow) {
                                            this.windowCommunicator.singlePostToWindow(this.tipLinkIframe.contentWindow, {
                                                type: "tiplink_autoconnect_from_redirect",
                                                title: this.title,
                                            });
                                        }
                                        // console.log(
                                        //   "displaying iframe for tiplink autoconnect from redirect after",
                                        //   Date.now() - start
                                        // );
                                        this.showIframe();
                                    }
                                },
                            },
                            ready_for_tiplink_autoconnect: {
                                type: CallbackType.DEFAULT,
                                cb: async (data) => {
                                    // console.log(
                                    //   "received ready_for_tiplink_autoconnect",
                                    //   data
                                    // );
                                    if (this.tipLinkIframe?.contentWindow) {
                                        this.windowCommunicator.singlePostToWindow(this.tipLinkIframe.contentWindow, {
                                            type: "ack",
                                            title: this.title,
                                        });
                                    }
                                },
                            },
                            loaded_public_key: {
                                type: CallbackType.DEFAULT,
                                cb: async (data) => {
                                    clearInterval(checkPopupClosed);
                                    // console.log("RECEIVED LOADED PUBLIC_KEY");
                                    this.hideIframe();
                                    if (this.tipLinkIframe?.contentWindow) {
                                        this.windowCommunicator.singlePostToWindow(this.tipLinkIframe.contentWindow, {
                                            type: "ack_loaded_public_key",
                                            title: this.title,
                                            dAppSessionId: this.dAppSessionId,
                                            tipLinkSessionId: this.tipLinkSessionId,
                                        });
                                    }
                                    // console.log(
                                    //   'received "loaded_public_key" message from iframe',
                                    //   data.publicKey
                                    // );
                                    this.publicKeyString = data.publicKey;
                                    if (siwsInputPromise) {
                                        const siwsOutput = await this._signIn(siwsInputPromise, true);
                                        if (!siwsOutput) {
                                            reject("missing siwsOutput");
                                        }
                                        resolve({
                                            pk: data.publicKey,
                                            siwsOutput,
                                        });
                                    }
                                    resolve({
                                        pk: data.publicKey,
                                    });
                                },
                            },
                            public_key: {
                                type: CallbackType.DEFAULT,
                                cb: async (data) => {
                                    clearInterval(checkPopupClosed);
                                    // console.log("RECEIVED PUBLIC_KEY");
                                    this.hideIframe();
                                    // console.log(
                                    //   'received "public_key" message from iframe',
                                    //   data.publicKey
                                    // );
                                    this.publicKeyString = data.publicKey;
                                    if (promptTipLinkAutoConnectFromRedirect ||
                                        tipLinkAutoConnect) {
                                        this.showTipLinkAutoconnectToast();
                                    }
                                    if (siwsInputPromise) {
                                        const siwsOutput = await this._signIn(siwsInputPromise, true);
                                        if (!siwsOutput) {
                                            reject("missing siwsOutput");
                                        }
                                        resolve({
                                            pk: data.publicKey,
                                            siwsOutput,
                                        });
                                    }
                                    resolve({
                                        pk: data.publicKey,
                                    });
                                },
                            },
                            cancel_connect: {
                                type: CallbackType.DEFAULT,
                                cb: async (data) => {
                                    // console.log("RECEIVED CANCEL_CONNECT");
                                    this.hideIframe();
                                    windowParams?.popup?.close();
                                    this.cleanUp();
                                    reject("user clicked close button in iframe");
                                },
                            },
                            focus_login: {
                                type: CallbackType.DEFAULT,
                                cb: async (data) => {
                                    windowParams?.popup?.focus();
                                },
                            },
                        }, ["public_key", "cancel_connect", "loaded_public_key"]);
                        fnsAtEnd.push(closeIframeChannel);
                    });
                }
                catch (error) {
                    // console.log("REJECTING error", error);
                    // console.log("rejected via errior");
                    reject(error);
                }
            });
        };
        // console.log("readying document");
        await documentReady();
        // console.log("handling setup");
        return handleSetup().then((result) => {
            fnsAtEnd.forEach((fn) => fn());
            return result;
        });
    }
    async cleanUp() {
        // console.log("cleaning up");
        if (this.tipLinkIframe) {
            const iFrame = this.tipLinkIframe;
            await new Promise((resolve) => {
                const { post: postToIframe } = this.windowCommunicator.setupHandshakeWithIframe(iFrame, {
                    disconnected: {
                        type: CallbackType.DEFAULT,
                        cb: async () => {
                            resolve();
                        },
                    },
                }, ["disconnected"]);
                postToIframe({ type: "disconnect" });
            });
        }
        this.publicKeyString = undefined;
        this.clearElements();
    }
    clearElements() {
        localStorage.removeItem("tipLink_pk_connected");
        if (this.tipLinkIframe === undefined)
            throw new Error("TipLink IFrame not initialized");
        if (this.tipLinkAlertContainer === undefined)
            throw new Error("TipLink Alert Container not initialized");
        if (this.tipLinkToastContainer === undefined)
            throw new Error("TipLink Toast Container not initialized");
        if (this.styleLink === undefined) {
            throw new Error("TipLink Styles not applied");
        }
        function isElement(element) {
            return element instanceof Element || element instanceof Document;
        }
        // console.log("trying to clear elements");
        if (this.styleLink &&
            isElement(this.styleLink) &&
            window.document.head.contains(this.styleLink)) {
            this.styleLink.remove();
            this.styleLink = undefined;
        }
        if (this.tipLinkIframe &&
            isElement(this.tipLinkIframe) &&
            window.document.body.contains(this.tipLinkIframe)) {
            this.tipLinkIframe.remove();
            this.tipLinkIframe = undefined;
        }
        if (this.tipLinkAlertContainer &&
            isElement(this.tipLinkAlertContainer) &&
            window.document.body.contains(this.tipLinkAlertContainer)) {
            this.tipLinkAlertContainer.remove();
            this.tipLinkAlertContainer = undefined;
        }
        if (this.tipLinkToastContainer &&
            isElement(this.tipLinkToastContainer) &&
            window.document.body.contains(this.tipLinkToastContainer)) {
            this.tipLinkToastContainer.remove();
            this.tipLinkToastContainer = undefined;
        }
    }
    async buildTransactionMessage(transaction) {
        if (isVersionedTransaction(transaction)) {
            return {
                message: Buffer.from(transaction.serialize()).toString("base64"),
            };
        }
        return {
            message: transaction
                .serialize({ requireAllSignatures: false })
                .toString("base64"),
        };
    }
    async _signTransaction({ transaction, connectionNodePublicKeys, }) {
        this.extendSession();
        // console.log("signing transaction");
        const msg = await this.buildTransactionMessage(transaction);
        return await new Promise((resolve, reject) => {
            if (!this.tipLinkIframe) {
                reject();
                return;
            }
            this.showIframe();
            const requestId = uuid();
            const { post } = this.windowCommunicator.setupHandshakeWithIframe(this.tipLinkIframe, {
                signed_transaction: {
                    type: CallbackType.DEFAULT,
                    cb: async (data) => {
                        this.hideIframe();
                        resolve(data.signed_transaction);
                    },
                },
                transaction_closed: {
                    type: CallbackType.DEFAULT,
                    cb: async () => {
                        this.hideIframe();
                        reject("User rejected transaction");
                    },
                },
                sign_error: {
                    type: CallbackType.DEFAULT,
                    cb: async (data) => {
                        this.hideIframe();
                        reject(data.message);
                    },
                },
            }, ["signed_transaction", "transaction_closed", "sign_error"], requestId);
            post({
                ...msg,
                type: "sign_transaction",
                connectionNodePublicKeys,
                requestId,
            });
        });
    }
    transactionFromString(isVersioned, signedTransactionMsg) {
        if (isVersioned) {
            return VersionedTransaction.deserialize(Buffer.from(signedTransactionMsg, "base64"));
        }
        else {
            return Transaction.from(Buffer.from(signedTransactionMsg, "base64"));
        }
    }
    async signTransaction(transaction) {
        const signedTransaction = await this._signTransaction({ transaction });
        return this.transactionFromString(isVersionedTransaction(transaction), signedTransaction);
    }
    async signAllTransactions(transactions) {
        this.extendSession();
        const messages = await Promise.all(transactions.map(async (transaction) => {
            const isVersioned = isVersionedTransaction(transaction);
            const { message } = await this.buildTransactionMessage(transaction);
            return {
                message,
                isVersioned,
            };
        }));
        const signedTxnMessages = await new Promise((resolve, reject) => {
            if (!this.tipLinkIframe) {
                reject();
                return;
            }
            this.showIframe();
            const requestId = uuid();
            const { post } = this.windowCommunicator.setupHandshakeWithIframe(this.tipLinkIframe, {
                signed_transactions: {
                    type: CallbackType.DEFAULT,
                    cb: async (data) => {
                        this.hideIframe();
                        resolve(data.signed_transactions);
                    },
                },
                transaction_closed: {
                    type: CallbackType.DEFAULT,
                    cb: async () => {
                        this.hideIframe();
                        reject("User rejected transaction");
                    },
                },
                sign_error: {
                    type: CallbackType.DEFAULT,
                    cb: async (data) => {
                        this.hideIframe();
                        reject(data.message);
                    },
                },
            }, ["signed_transactions", "transaction_closed", "sign_error"], requestId);
            post({
                type: "sign_all_transactions",
                messages: messages.map((msg) => msg.message),
                requestId,
            });
        });
        return signedTxnMessages.map((signedTxnMsg, i) => {
            return this.transactionFromString(messages[i].isVersioned, signedTxnMsg);
        });
    }
    async _signMessage(message, type, skipConfirm) {
        return new Promise((resolve, reject) => {
            // TODO: do we still need to extend session
            if (!this.tipLinkIframe) {
                reject("iframe is missing");
                return;
            }
            this.extendSession();
            const requestId = uuid();
            const { post } = this.windowCommunicator.setupHandshakeWithIframe(this.tipLinkIframe, {
                signed_message: {
                    type: CallbackType.DEFAULT,
                    cb: async (data) => {
                        this.hideIframe();
                        const signedMessage = Buffer.from(data.signed_message, "base64");
                        resolve({ data: signedMessage, extraInfo: data.extraInfo });
                    },
                },
                message_closed: {
                    type: CallbackType.DEFAULT,
                    cb: async () => {
                        this.hideIframe();
                        reject("User rejected message");
                    },
                },
                sign_error: {
                    type: CallbackType.DEFAULT,
                    cb: async (data) => {
                        this.hideIframe();
                        reject(data.message);
                    },
                },
            }, ["signed_message", "message_closed", "sign_error"], requestId);
            this.showIframe();
            post({
                message: Buffer.from(message).toString("base64"),
                type,
                requestId,
                skipConfirm,
            });
        });
    }
    async signMessage(message) {
        const { data } = await this._signMessage(message, "sign_message");
        return { signature: data };
    }
    async _signIn(customInput, skipConfirm) {
        const input = await customInput;
        const publicKeyAddress = input?.address || this.publicKeyString;
        if (!publicKeyAddress) {
            throw new Error("not connected!");
        }
        const domain = input?.domain || window.location.host;
        if (!domain) {
            throw new Error("no domain found!");
        }
        const siwsRequiredFields = {
            ...input,
            domain,
            address: publicKeyAddress,
        };
        const signInMessage = createSignInMessage(siwsRequiredFields);
        const { data: signature, extraInfo } = await this._signMessage(signInMessage, "siws", skipConfirm);
        return {
            account: new ReadonlyWalletAccount({
                address: publicKeyAddress,
                publicKey: new PublicKey(publicKeyAddress).toBytes(),
                chains: [SOLANA_MAINNET_CHAIN],
                // These must be included, otherwise the Standard Wallet Adapter
                // will assume these features don't exist on the wallet adapter
                features: [
                    SolanaSignAndSendTransaction,
                    SolanaSignTransaction,
                    SolanaSignMessage,
                    SolanaSignIn,
                ],
            }),
            signedMessage: signInMessage,
            signature,
            // @ts-ignore
            extraInfo,
        };
    }
    async signIn(input) {
        return this._signIn(input);
    }
    // This is copied from the `sendTransaction` method in BaseSignerWalletAdapter,
    // with the changes to use our own internal _signTransaction method, and to pass in the
    // cluster nodes for the connectino that was passed into `sendTransaction`.
    // we also don't emit an error since the method calling this is responsible for that.
    async sendTransaction(transaction, prepareTransaction, connection, options = {}) {
        if (isVersionedTransaction(transaction)) {
            try {
                const transactionString = await this._signTransaction({
                    transaction,
                    connectionNodePublicKeys: (await connection.getClusterNodes()).map((x) => x.pubkey),
                });
                const rawTransaction = Buffer.from(transactionString, "base64");
                return await connection.sendRawTransaction(rawTransaction, options);
            }
            catch (error) {
                if (error instanceof WalletSignTransactionError) {
                    throw error;
                }
                throw new WalletSendTransactionError(error?.message, error);
            }
        }
        else {
            try {
                const { signers, ...sendOptions } = options;
                const txn = await prepareTransaction(transaction, connection, sendOptions);
                signers?.length && txn.partialSign(...signers);
                const nodes = await connection.getClusterNodes();
                const transactionString = await this._signTransaction({
                    transaction: txn,
                    connectionNodePublicKeys: nodes.map((x) => x.pubkey),
                });
                const rawTransaction = Buffer.from(transactionString, "base64");
                return await connection.sendRawTransaction(rawTransaction, sendOptions);
            }
            catch (error) {
                if (error instanceof WalletSignTransactionError) {
                    throw error;
                }
                throw new WalletSendTransactionError(error?.message, error);
            }
        }
    }
    notifyDisallowed() {
        try {
            this.clearElements();
        }
        catch {
            // best effort
        }
        console.error(window.location.origin, "not allowlisted – please contact the TipLink team at contact@tiplink.io to be added.");
        showDialog(this.buildEnv, `<p>${window.location.origin} does not have access yet ` +
            "to use the TipLink Wallet Adapter. Please reach out to the " +
            'TipLink team at <a style="text-decoration: underline;" href="mailto:contact@tiplink.io" target="_blank"> ' +
            "contact@tiplink.io</a> or reach out via our " +
            '<a style="text-decoration: underline;" href="https://discord.com/invite/4bXYT7dxR3" target="_blank" >' +
            "discord</a>.</p>");
    }
}
//# sourceMappingURL=embed.js.map